import { type AxiosResponse } from 'axios'
import type { AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'
import { type IResponseGetAgentUsage } from 'entities/AICalling/api/types'

import {
  type IChatbotPayload,
  type IParamsGetChatbotAnalyticList,
  type IParamsGetChatbotList,
  type IResponseChatbotAnalyticList,
  type IResponseChatbotList,
  type IChatbotFullData,
  type IResponseDeleteChatbot,
  type IResponseUpdateChatbotStatus,
  type IParamsEnrollContactsInChatbot,
  type IResponseChatbotInstruction,
  type IResponseChatbotValidation,
  type IParamsValidateAgentByStep,
  type IParamsEnrollContactsByFiltersInChatbot,
  type IParamsEnhancePrompt,
  type IResponseEnhancePrompt,
  type IParamsValidateUnifiedAgent,
  type IResponseValidateUnifiedPrompt,
  type IParamsUnifiedEnhancePrompt,
  type IResponseCreateChatSession,
  type IResponseGetSessionMessage,
  type IParamsSendSessionMessage,
  type IResponseSendSessionMessage,
  type IResponseBannerStatus,
  type IResponseDismissBanner,
} from './chatbotTypes'
import { ChatbotInstructionType, type IAgentPayload } from '../model/type'

class Api {
  getChatbotList(
    params: IParamsGetChatbotList,
    options?: Omit<AxiosOptions, 'params'>
  ): Promise<AxiosResponse<IResponseChatbotList>> {
    return baseApi.get('chatbot/api/chatbots', { ...options, params })
  }

  getChatbot(id: number, options?: AxiosOptions): Promise<AxiosResponse<IChatbotFullData>> {
    return baseApi.get(`chatbot/api/chatbots/${id}`, options)
  }

  createChatbot(data: IChatbotPayload): Promise<AxiosResponse<IChatbotFullData>> {
    return baseApi.post('chatbot/api/chatbots', data)
  }

  updateChatbot(id: number, data: IChatbotPayload): Promise<AxiosResponse<IChatbotFullData>> {
    return baseApi.put(`chatbot/api/chatbots/${id}`, data)
  }

  enrollChatbot(
    id: number,
    data?: IParamsEnrollContactsByFiltersInChatbot
  ): Promise<AxiosResponse<void>> {
    return baseApi.post(`chatbot/api/chatbots/enroll/${id}`, data)
  }

  updateChatbotStatus(
    id: number,
    isActive: boolean
  ): Promise<AxiosResponse<IResponseUpdateChatbotStatus>> {
    return baseApi.put(`chatbot/api/chatbots/status/${id}`, { is_active: isActive })
  }

  deleteChatbot(id: number): Promise<AxiosResponse<IResponseDeleteChatbot>> {
    return baseApi.delete(`chatbot/api/chatbots/${id}`)
  }

  getAgentUsage(id: number): Promise<AxiosResponse<IResponseGetAgentUsage>> {
    return baseApi.get(`voice/ai-texting-agent/usage/${id}`)
  }

  getChatbotAnalyticList(
    { id, ...params }: IParamsGetChatbotAnalyticList,
    options?: Omit<AxiosOptions, 'params'>
  ): Promise<AxiosResponse<IResponseChatbotAnalyticList>> {
    return baseApi.get(`chatbot/api/chatbots/analytics/list/${id}`, { ...options, params })
  }

  enrollContactsInChatbot(
    { chatbot_id, ...params }: IParamsEnrollContactsInChatbot,
    options?: Omit<AxiosOptions, 'params'>
  ) {
    return baseApi.post(`chatbot/api/chatbots/enroll-by-contact-ids/${chatbot_id}`, params, options)
  }

  getInstructions(): Promise<AxiosResponse<IResponseChatbotInstruction>> {
    return baseApi.get('agents/auth/api/v1/prompt/templates')
  }

  validateNewAgentByStep(
    step: string,
    params: IParamsValidateAgentByStep
  ): Promise<AxiosResponse<IResponseChatbotValidation>> {
    return baseApi.post(`chatbot/api/chatbots/steps/validate/${step}`, {
      ...params,
      type: ChatbotInstructionType.BookAppointment,
      is_active: true,
    })
  }

  validateExistingAgentByStep(
    step: string,
    id: number,
    params: IParamsValidateAgentByStep
  ): Promise<AxiosResponse<IResponseChatbotValidation>> {
    return baseApi.post(`chatbot/api/chatbots/steps/validate/${step}/${id}`, {
      ...params,
      type: ChatbotInstructionType.BookAppointment,
      is_active: true,
    })
  }

  createNewAgent(data: IAgentPayload): Promise<AxiosResponse<IChatbotFullData>> {
    return baseApi.post('chatbot/api/chatbots/steps', data)
  }

  updateAgent(id: number, data: IAgentPayload): Promise<AxiosResponse<IChatbotFullData>> {
    return baseApi.put(`chatbot/api/chatbots/steps/${id}`, data)
  }

  enhancePrompt(
    data: IParamsEnhancePrompt,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseEnhancePrompt>> {
    return baseApi.post('agents/auth/api/v1/prompt/improve', data, options)
  }

  enhanceUnifiedPrompt(
    data: IParamsUnifiedEnhancePrompt,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseEnhancePrompt>> {
    return baseApi.post('agents/auth/api/v1/prompt/unified/enhance', data, options)
  }

  validateUnifiedAgent(
    data: IParamsValidateUnifiedAgent
  ): Promise<AxiosResponse<IResponseValidateUnifiedPrompt>> {
    return baseApi.post('agents/auth/api/v1/prompt/unified/validate', data)
  }

  createChatSession(): Promise<AxiosResponse<IResponseCreateChatSession>> {
    return baseApi.post('agents/auth/api/v1/chatbot-creator/sessions')
  }

  getSessionMessage(session_id: string): Promise<AxiosResponse<IResponseGetSessionMessage>> {
    return baseApi.get(`agents/auth/api/v1/chatbot-creator/sessions/${session_id}/messages`)
  }

  sendSessionMessage(
    session_id: string,
    params: IParamsSendSessionMessage
  ): Promise<AxiosResponse<IResponseSendSessionMessage>> {
    return baseApi.post(
      `agents/auth/api/v1/chatbot-creator/sessions/${session_id}/messages`,
      params
    )
  }

  getBannerStatus(): Promise<AxiosResponse<IResponseBannerStatus>> {
    return baseApi.get('chatbot/api/banners/booking-meetings')
  }

  setDismiss(): Promise<AxiosResponse<IResponseDismissBanner>> {
    return baseApi.post('chatbot/api/banners/booking-meetings/dismiss')
  }
}

export const ChatbotApi = new Api()
